import React, { useState, useEffect } from 'react';
import {
  Box,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { ADMIN_SIDEBAR_ITEMS, DOCUMENTATION_SIDEBAR_ITEMS, SIDEBAR_ITEMS } from '../../ui/layout/sidebar/SidebarMenuItems';
import { tokens } from '../../mui-theme';
import { useAccess } from '../../hooks/useAccess';

export const MainListItems = ({ projectId, open, type, showIcons, onSidebarNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [submenuOpen, setSubmenuOpen] = useState({});
  const { canAccess, isReady } = useAccess();

  const isDashboardPage = location.pathname.includes('/dashboard');

  const isSubmenuActive = (param) => {
    const params = new URLSearchParams(location.search);
    return params.get('view') === param;
  };

  useEffect(() => {
    if (!open) return;
    setSubmenuOpen((prev) => ({ ...prev, home: isDashboardPage }));
  }, [isDashboardPage, open]);

  const handleItemClick = (item) => {
    if (!open) return navigate(item.url);
    if (item.submenu) {
      if (type !== 'documentation') navigate(item.url);
      setSubmenuOpen((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
    } else {
      navigate(item.url);
    }
  };

  const handleSubmenuClick = (param, mainItemUrl) => {
    if (type === 'documentation' && onSidebarNavigate) {
      onSidebarNavigate(param);
    } else {
      const currentParams = new URLSearchParams(location.search);
      currentParams.set('view', param);
      navigate(`${mainItemUrl}?${currentParams.toString()}`);
    }
  };

  let sidebarItems = [];
  if (type === 'admin-dashboard') sidebarItems = ADMIN_SIDEBAR_ITEMS(canAccess, isReady);
  else if (type === 'documentation') sidebarItems = DOCUMENTATION_SIDEBAR_ITEMS();
  else sidebarItems = SIDEBAR_ITEMS(projectId, canAccess, isReady);

  return (
    <>
      {sidebarItems.map((item) => {
        const isActive = location.pathname === item.url;
        const hasSubmenu = !!item.submenu;
        const isExpanded = submenuOpen[item.id];

        return (
          <React.Fragment key={item.id}>
            {/* ── Main item ── */}
            <Box sx={{ px: open ? 1 : 0.5, mb: 0.25 }}>
              <Tooltip
                title={!open ? item.label : ''}
                placement="right"
                arrow
              >
                <ListItemButton
                  onClick={() => handleItemClick(item)}
                  selected={isActive}
                  sx={{
                    borderRadius: '6px',
                    py: 0.75,
                    px: open ? 1.5 : 1,
                    minHeight: 40,
                    position: 'relative',
                    justifyContent: open ? 'flex-start' : 'center',
                    backgroundColor: isActive
                      ? alpha(tokens.colors.primary[500], 0.08)
                      : 'transparent',
                    transition: 'background-color 0.15s',
                    '&:hover': {
                      backgroundColor: isActive
                        ? alpha(tokens.colors.primary[500], 0.11)
                        : tokens.colors.neutral[100],
                    },
                    '&.Mui-selected': {
                      backgroundColor: alpha(tokens.colors.primary[500], 0.08),
                      '&:hover': {
                        backgroundColor: alpha(tokens.colors.primary[500], 0.11),
                      },
                    },
                    ...(isActive && open && {
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '18%',
                        height: '64%',
                        width: 3,
                        borderRadius: '0 3px 3px 0',
                        backgroundColor: tokens.colors.primary[500],
                      },
                    }),
                  }}
                >
                  {showIcons && (
                    <ListItemIcon
                      sx={{
                        minWidth: open ? 36 : 'unset',
                        color: isActive
                          ? tokens.colors.primary[500]
                          : tokens.colors.neutral[500],
                        '& svg': { fontSize: 20 },
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                  )}

                  {open && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.8125rem',
                        fontWeight: isActive ? 600 : 400,
                        color: isActive
                          ? tokens.colors.primary[500]
                          : tokens.colors.neutral[700],
                        lineHeight: 1.4,
                        noWrap: true,
                      }}
                      sx={{ m: 0 }}
                    />
                  )}

                  {hasSubmenu && open && (
                    <Box
                      sx={{
                        color: isActive
                          ? tokens.colors.primary[500]
                          : tokens.colors.neutral[400],
                        display: 'flex',
                        alignItems: 'center',
                        ml: 0.5,
                      }}
                    >
                      {isExpanded
                        ? <ExpandLess sx={{ fontSize: 16 }} />
                        : <ExpandMore sx={{ fontSize: 16 }} />}
                    </Box>
                  )}
                </ListItemButton>
              </Tooltip>
            </Box>

            {/* ── Submenu ── */}
            {hasSubmenu && open && (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ pb: 0.5 }}>
                  {item.submenu.map((submenuItem) => {
                    const isSubActive = isSubmenuActive(submenuItem.param);
                    return (
                      <Box key={submenuItem.id} sx={{ pl: 3, pr: 1, mb: 0.25 }}>
                        <ListItemButton
                          onClick={() => handleSubmenuClick(submenuItem.param, item.url)}
                          selected={isSubActive}
                          sx={{
                            borderRadius: '6px',
                            py: 0.6,
                            px: 1.5,
                            minHeight: 32,
                            backgroundColor: isSubActive
                              ? alpha(tokens.colors.primary[500], 0.07)
                              : 'transparent',
                            transition: 'background-color 0.15s',
                            '&:hover': {
                              backgroundColor: isSubActive
                                ? alpha(tokens.colors.primary[500], 0.1)
                                : tokens.colors.neutral[100],
                            },
                            '&.Mui-selected': {
                              backgroundColor: alpha(tokens.colors.primary[500], 0.07),
                            },
                            borderLeft: `2px solid ${isSubActive
                              ? tokens.colors.primary[400]
                              : tokens.colors.neutral[200]}`,
                          }}
                        >
                          <ListItemText
                            primary={submenuItem.label}
                            primaryTypographyProps={{
                              fontSize: '0.78rem',
                              fontWeight: isSubActive ? 600 : 400,
                              color: isSubActive
                                ? tokens.colors.primary[500]
                                : tokens.colors.neutral[600],
                              lineHeight: 1.4,
                            }}
                            sx={{ m: 0 }}
                          />
                        </ListItemButton>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};